import { Check } from "cesium";

/**
 * An object containing constants for Google Analytics events.
 * @typedef {Object} GoogleAnalyticsEvents
 * @property {string} SC_CLICK - The event name for when a click occurs in the SC Design3D.
 */

/**
 * An object containing constants for Google Analytics events.
 * @type {GoogleAnalyticsEvents}
 */
export const GoogleAnalyticsEvents = Object.freeze({
	SC_CLICK: "sc_click",
});

/**
 * @typedef {Object} GoogleAnalyticsClickTypes
 * @property {string} SC_JOBSITE_OPEN - The event name for when a project is open.
 */

/**
 * Object containing click types for Google Analytics events.
 * @type {GoogleAnalyticsClickTypes}
 */
export const GoogleAnalyticsClickTypes = Object.freeze({
	SC_JOBSITE_OPEN: "sc_jobsite_open", // 現場一覧でプロジェクトOpenボタン押下時
	P_CLICK_TERRAIN: "p_click_terrain", // 点群データボタン押下時
	P_CLICK_DESIGN: "p_click_design", // 設計データボタン押下時
	P_CLICK_2D_IMAGE: "p_click_2d_image", // 2次元画像ボタン押下時
	P_CLICK_REFERENCE_TOPOLOGY: "p_click_reference_topology", // 基準平面ボタン押下時
	P_CLICK_LINE_WORKS: "p_click_line_works", // ラインワークスボタン押下時
	P_CLICK_GROUPWARE: "p_click_groupware", // Groupwareからインポートボタン押下時
	P_CLICK_FROM_DASHBOARD: "p_click_from_dashboard", // Dahboardからデータ取り込みボタン押下時
	P_CLICK_ADD_ROAD: "p_click_add_road", // 仮設道路の追加ボタン押下時
	P_CLICK_ADD_PLANE: "p_click_add_plane", // 平場の追加の追加ボタン押下時
	P_CLICK_ADD_TRENCH: "p_click_add_trench", // 床掘の追加の追加ボタン押下時
	P_CLICK_LINE_UPLOAD: "p_click_line_upload", // 線形のアップロードボタン押下時
	P_CLICK_LINE_CREATE: "p_click_line_create", // 線形の任意作成ボタン押下時
	P_CLICK_LINE_OBJECT: "p_click_line_object", // オブジェクトから線形出力ボタン押下時
	P_CLICK_OBJECT_EXPORT_XML: "p_click_object_export_xml", // オブジェクトに対し、LandXML出力を選択時
	P_CLICK_OBJECT_EXPORT_DXF: "p_click_object_export_dxf", // オブジェクトに対し、DXF出力を選択時
	P_CLICK_OBJECT_EXPORT_DASHBOARD: "p_click_object_export_dashboard", // オブジェクトに対し、Dashboard出力を選択時
	P_CLICK_OBJECT_EXPORT_DASHBOARD_DXF: "p_click_object_export_dashboard_dxf", // オブジェクトに対し、Dashboard DXF出力を選択時
	P_CLICK_OBJECT_EXPORT_GROUPWARE: "p_click_object_export_groupware", // オブジェクトに対し、Simulation連携を選択時
	P_CLICK_OBJECT_EXPORT_IFC: "p_click_object_export_ifc", // オブジェクトに対し、IFC出力を選択時
	P_CLICK_OBJECT_EXPORT_GROUPWARE_DXF: "p_click_object_export_groupware_dxf", // オブジェクトに対し、Simulation DXF連携を選択時
	P_CLICK_OBJECT_LINE_CREATE: "p_click_object_line_create", // オブジェクトに対し、中心線形作成メニューを押下時
	P_CLICK_OBJECT_EDIT: "p_click_object_edit", // オブジェクトに対し、編集メニューを押下時
	P_CLICK_OBJECT_DELETE: "p_click_object_delete", // オブジェクトに対し、削除メニューを押下時
	P_CLICK_OBJECT_VOLUME_CALC: "p_click_object_volume_calc", // オブジェクトに対し、土量計算ボタン押下時
	P_CLICK_OBJECT_AREA_CALC: "p_click_object_area_calc", // オブジェクトに対し、表面積計算ボタン押下時
	P_CLICK_LINE_EXPORT: "p_click_line_export", // 線形に対し、出力メニュー押下時
	P_CLICK_LINE_CROSS_SECTION_EXPORT: "p_click_line_cross_section_export", // 線形に対し、断面出力メニュー押下時
	P_CLICK_LINE_EDIT: "p_click_line_edit", // 線形に対し、編集メニュー押下時
	P_CLICK_LINE_DELETE: "p_click_line_delete", // 線形に対し、削除メニュー押下時
	P_CLICK_VIEW_LONGITUDINAL: "p_click_view_longitudinal", // 2次元Viewに対し、縦断メニュー押下時
	P_CLICK_VIEW_CROSS_SECTION: "p_click_view_cross_section", // 2次元Viewに対し、断面メニュー押下時
	P_CLICK_VIEW_LINE_LONGITUDINAL: "p_click_view_line_longitudinal", // 2次元Viewに対し、線形縦断メニュー押下時
	P_CLICK_VIEW_LINE_CROSS_SECTION: "p_click_view_line_cross_section", // 2次元Viewに対し、線形断面メニュー押下時
	P_CLICK_VIEW_CUSTOM_CROSS_SECTION: "p_click_view_custom_cross_section", // 2次元Viewに対し、任意断面メニュー押下時
	P_CLICK_VIEW_SEPARATE: "p_click_view_separate", // 2次元Viewに対し、分離ボタン押下時
	P_CLICK_LINK_DASHBOARD: "p_click_link_dashboard", // Dashboardリンクメニュー押下時
	P_CLICK_LINK_SIMULATION: "p_click_link_simulation", // Simulationリンクメニュー押下時
	P_CLICK_LINE_MEASUREMENT: "p_click_line_measurement", // ２点間計測ボタン押下時
	P_CLICK_2D_EDIT: "p_click_2d_edit", // ２次元画像に対し、編集メニュー押下時
	P_CLICK_2D_2POINT_ALIGNMENT: "p_click_2d_2point_alignment", // ２次元画像に対し、２点アラインメントメニュー押下時
	P_CLICK_2D_EXPORT_XML: "p_click_2d_export_xml", // ２次元画像に対し、LandXML出力を選択時
	P_CLICK_2D_EXPORT_GEOTIFF: "p_click_2d_export_geotiff", // ２次元画像に対し、GEOTIFF出力を選択時
	P_CLICK_2D_EXPORT_DASHBOARD: "p_click_2d_export_dashboard", // ２次元画像に対し、Dashboard出力を選択時
	P_CLICK_2D_DELETE: "p_click_2d_delete", // ２次元画像に対し、削除メニュー押下時
	P_CLICK_ADD_SIMPLE_STRUCTURE_BOX_CULVERT:
		"p_click_add_simple_structure_box_culvert", // 簡易構造物でボックスカルバートを追加時
	P_CLICK_ADD_SIMPLE_STRUCTURE_MANHOLE: "p_click_add_simple_structure_manhole", // 簡易構造物でマンホールを追加時
	P_CLICK_ADD_SIMPLE_STRUCTURE_PIPE: "p_click_add_simple_structure_pipe", // 簡易構造物で配管を追加時
	P_CLICK_ADD_SIMPLE_STRUCTURE_RETAINING_WALL:
		"p_click_add_simple_structure_retaining_wall", // 簡易構造物で擁壁を追加時
	P_CLICK_ADD_SIMPLE_STRUCTURE_MANUAL: "p_click_add_simple_structure_manual", // 簡易構造物でカスタム構造物を追加時
});

/**
 * Object containing keys for Google Analytics.
 * @typedef {Object} GoogleAnalyticsKeys
 * @property {string} EVENT - The key for the event.
 * @property {string} CLICKTYPE - The key for the parameters.
 */

/**
 * Object containing keys for Google Analytics.
 * @type {GoogleAnalyticsKey}
 */
export const GoogleAnalyticsKey = Object.freeze({
	EVENT: "event",
	CLICKTYPE: "clicktype",
});

export const ENVIRONMENTS = Object.freeze({
	LOCAL: "local",
	DEV: "dev",
	STG: "stg",
	PROD: "prod",
});

export function getEnvironment(appUrl) {
	if (appUrl.includes("localhost")) return ENVIRONMENTS.LOCAL;
	if (appUrl.includes("-dev.")) return ENVIRONMENTS.DEV;
	if (appUrl.includes("-stg.")) return ENVIRONMENTS.STG;
	return ENVIRONMENTS.PROD;
}

/**
 * Adds logs to the data layer.
 * @param {Object} keyValue - The key-value pair to be added to the data layer.
 * @param {GoogleAnalyticsKey} keyValue.key - The key for the data layer.
 */

export const addLogsToGoogleAnalytics = (keyValue) => {
	//>>includeStart('debug', pragmas.debug);
	Check.typeOf.object("keyValue", keyValue);
	const keys = Object.keys(keyValue);
	const allowedKeys = Object.values(GoogleAnalyticsKey);
	const invalidKeys = keys.filter((key) => !allowedKeys.includes(key));
	if (invalidKeys.length > 0) {
		console.error(
			`Invalid key "${invalidKeys[0]}". Only keys from GoogleAnalyticsKeys are allowed.`,
		);
	}
	//>>includeEnd('debug');

	const appUrl = addLogsToGoogleAnalytics._getAppUrl();

	const isStgOrProd = [ENVIRONMENTS.STG, ENVIRONMENTS.PROD].includes(
		getEnvironment(appUrl),
	);
	if (!isStgOrProd && import.meta.env?.EXECUTION !== "vitest") {
		return;
	}

	window.dataLayer = window.dataLayer || [];
	window.dataLayer.push(keyValue);
};

export const addClickTypeLog = (value) => {
	addLogsToGoogleAnalytics({
		[GoogleAnalyticsKey.EVENT]: GoogleAnalyticsEvents.SC_CLICK,
		[GoogleAnalyticsKey.CLICKTYPE]: value,
	});
};

// for test
addLogsToGoogleAnalytics._getAppUrl = () => window.location.origin;
